import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { apiClient } from '../../App';
import Layout from '../../components/Layout';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { 
  Users, Target, Zap, Award, Lightbulb, Settings, 
  TrendingUp, CheckCircle2, Calendar, Trophy, Crown,
  ChevronRight, History, Bell, PartyPopper
} from 'lucide-react';
import { triggerCelebration } from '../../components/Celebration';
import GifCelebrationModal, { CELEBRATION_GIFS } from '../../components/GifCelebrationModal';
import AdminWeeklyIntentions from './AdminWeeklyIntentions';

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentLeaderboard, setCurrentLeaderboard] = useState(null);
  const [historicalMonths, setHistoricalMonths] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState('current');
  const [historicalLeaderboard, setHistoricalLeaderboard] = useState(null);
  const [celebrationType, setCelebrationType] = useState('random');
  const [showGifModal, setShowGifModal] = useState(false);
  const [testGifUrl, setTestGifUrl] = useState('');
  const navigate = useNavigate();

  const celebrationTypes = [
    { value: 'random', label: '🎲 Random (Surprise me!)' },
    { value: 'confetti', label: '🎊 Confetti Burst' },
    { value: 'fireworks', label: '🎆 Fireworks' },
    { value: 'stars', label: '⭐ Stars' },
    { value: 'emoji', label: '🎉 Emoji Party' },
    { value: 'rainbow', label: '🌈 Rainbow' },
    { value: 'gif', label: '🖼️ GIF (Random sample)' },
  ];

  const handleTestCelebration = () => {
    if (celebrationType === 'gif') {
      // Pick a random GIF from the presets
      const randomGif = CELEBRATION_GIFS[Math.floor(Math.random() * CELEBRATION_GIFS.length)];
      setTestGifUrl(randomGif.url);
      setShowGifModal(true);
    } else {
      const type = celebrationType === 'random' ? null : celebrationType;
      triggerCelebration(type);
    }
  };

  useEffect(() => {
    fetchStats();
    fetchCurrentLeaderboard();
    fetchHistoricalMonths();
  }, []);

  useEffect(() => {
    if (selectedMonth !== 'current') {
      fetchHistoricalLeaderboard(selectedMonth);
    } else {
      setHistoricalLeaderboard(null);
    }
  }, [selectedMonth]);

  const fetchStats = async () => {
    try {
      const res = await apiClient.get('/admin/stats');
      setStats(res.data);
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchCurrentLeaderboard = async () => {
    try {
      const res = await apiClient.get('/admin/leaderboard/current');
      setCurrentLeaderboard(res.data);
    } catch (err) {
      console.error('Failed to fetch leaderboard:', err);
    }
  };

  const fetchHistoricalMonths = async () => {
    try {
      const res = await apiClient.get('/admin/leaderboard/history');
      setHistoricalMonths(res.data);
    } catch (err) {
      console.error('Failed to fetch historical months:', err);
    }
  };

  const fetchHistoricalLeaderboard = async (yearMonth) => {
    try {
      const [year, month] = yearMonth.split('-');
      const res = await apiClient.get(`/admin/leaderboard/month/${year}/${month}`);
      setHistoricalLeaderboard(res.data);
    } catch (err) {
      console.error('Failed to fetch historical leaderboard:', err);
    }
  };

  const quickLinks = [
    { to: '/admin/challenges', icon: Target, label: 'Manage Challenges', color: 'bg-blue-500/20 text-blue-400' },
    { to: '/admin/actions', icon: Zap, label: 'Manage Actions', color: 'bg-green-500/20 text-green-400' },
    { to: '/admin/milestones', icon: Award, label: 'Manage Milestones', color: 'bg-purple-500/20 text-purple-400' },
    { to: '/admin/tips', icon: Lightbulb, label: 'Manage Tips', color: 'bg-yellow-500/20 text-yellow-400' },
    { to: '/admin/automations', icon: Settings, label: 'Automations', color: 'bg-orange-500/20 text-orange-400' },
    { to: '/admin/notifications', icon: Bell, label: 'Send Notifications', color: 'bg-cyan-500/20 text-cyan-400' },
  ];

  const displayedLeaderboard = selectedMonth === 'current' 
    ? currentLeaderboard?.leaderboard 
    : historicalLeaderboard?.leaderboard;

  const leaderboardTitle = selectedMonth === 'current'
    ? `${new Date().toLocaleString('default', { month: 'long', year: 'numeric' })} Leaderboard`
    : historicalLeaderboard?.display_name + ' Leaderboard';

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-[calc(100vh-64px)]">
          <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-4 md:p-8 max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8 animate-fade-in flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-stone-900 mb-2">ADMIN DASHBOARD</h1>
            <p className="text-stone-600">Manage your RxGRIT challenge platform</p>
          </div>
          
          {/* Test Celebration */}
          <div className="flex items-center gap-2 bg-white border border-stone-200 rounded-xl p-3">
            <Select value={celebrationType} onValueChange={setCelebrationType}>
              <SelectTrigger className="w-[180px] bg-stone-100 border-stone-300 text-stone-900">
                <SelectValue placeholder="Celebration type" />
              </SelectTrigger>
              <SelectContent className="bg-stone-100 border-stone-300">
                {celebrationTypes.map(type => (
                  <SelectItem key={type.value} value={type.value} className="text-stone-900 hover:bg-stone-200">
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              onClick={handleTestCelebration}
              className="bg-gradient-to-r from-[#EFB342] to-[#D99F30] text-black font-semibold hover:opacity-90"
              data-testid="test-celebration-btn"
            >
              <PartyPopper size={18} className="mr-2" />
              Test!
            </Button>
          </div>
        </div>

        {/* Stats Grid - Clickable */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Link 
            to="/admin/users"
            className="bg-white border border-stone-200 rounded-2xl p-6 animate-fade-in hover:border-[#EFB342]/50 transition-all cursor-pointer group"
            data-testid="stat-total-users"
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-[#EFB342]/20 rounded-xl flex items-center justify-center group-hover:bg-[#EFB342]/30 transition-colors">
                <Users className="text-[#EFB342]" size={24} />
              </div>
              <div>
                <p className="text-3xl font-bold text-stone-900">{stats?.total_users || 0}</p>
                <p className="text-xs text-stone-500 uppercase">Total Users</p>
                <p className="text-[10px] text-stone-400">{stats?.active_users || 0} active, {stats?.inactive_users || 0} inactive</p>
              </div>
            </div>
            <ChevronRight className="absolute top-4 right-4 text-stone-400 group-hover:text-[#EFB342] transition-colors" size={16} />
          </Link>
          
          <Link
            to="/admin/users?filter=active-today"
            className="bg-white border border-stone-200 rounded-2xl p-6 animate-fade-in stagger-1 hover:border-green-500/50 transition-all cursor-pointer group relative"
            data-testid="stat-active-today"
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-green-500/20 rounded-xl flex items-center justify-center group-hover:bg-green-500/30 transition-colors">
                <CheckCircle2 className="text-green-500" size={24} />
              </div>
              <div>
                <p className="text-3xl font-bold text-stone-900">{stats?.active_today || 0}</p>
                <p className="text-xs text-stone-500 uppercase">Active Today</p>
              </div>
            </div>
            <ChevronRight className="absolute top-4 right-4 text-stone-400 group-hover:text-green-500 transition-colors" size={16} />
          </Link>
          
          <Link
            to="/admin/checkins"
            className="bg-white border border-stone-200 rounded-2xl p-6 animate-fade-in stagger-2 hover:border-purple-500/50 transition-all cursor-pointer group relative"
            data-testid="stat-total-checkins"
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-purple-500/20 rounded-xl flex items-center justify-center group-hover:bg-purple-500/30 transition-colors">
                <Calendar className="text-purple-500" size={24} />
              </div>
              <div>
                <p className="text-3xl font-bold text-stone-900">{stats?.total_checkins || 0}</p>
                <p className="text-xs text-stone-500 uppercase">Total Check-ins</p>
              </div>
            </div>
            <ChevronRight className="absolute top-4 right-4 text-stone-400 group-hover:text-purple-500 transition-colors" size={16} />
          </Link>
          
          <button
            onClick={() => document.getElementById('leaderboard-section').scrollIntoView({ behavior: 'smooth' })}
            className="bg-white border border-stone-200 rounded-2xl p-6 animate-fade-in stagger-3 hover:border-orange-500/50 transition-all cursor-pointer group relative text-left"
            data-testid="stat-total-points"
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-orange-500/20 rounded-xl flex items-center justify-center group-hover:bg-orange-500/30 transition-colors">
                <TrendingUp className="text-orange-500" size={24} />
              </div>
              <div>
                <p className="text-3xl font-bold text-stone-900">{stats?.total_points_earned || 0}</p>
                <p className="text-xs text-stone-500 uppercase">Points Earned</p>
              </div>
            </div>
            <ChevronRight className="absolute top-4 right-4 text-stone-400 group-hover:text-orange-500 transition-colors" size={16} />
          </button>
        </div>

        {/* Leaderboard Section */}
        <div id="leaderboard-section" className="mb-8">
          <div className="bg-white border border-stone-200 rounded-2xl p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <Trophy className="text-[#EFB342]" size={24} />
                <h2 className="font-heading text-xl font-bold text-stone-900">LEADERBOARD</h2>
              </div>
              <Select value={selectedMonth} onValueChange={setSelectedMonth}>
                <SelectTrigger className="w-48 bg-stone-50 border-stone-300 text-stone-900">
                  <SelectValue placeholder="Select month" />
                </SelectTrigger>
                <SelectContent className="bg-stone-50 border-stone-200">
                  <SelectItem value="current" className="text-stone-700 focus:bg-[#EFB342]/20 focus:text-stone-900">
                    <div className="flex items-center gap-2">
                      <span>Current Month</span>
                    </div>
                  </SelectItem>
                  {historicalMonths.map((month) => (
                    <SelectItem 
                      key={month.year_month} 
                      value={month.year_month}
                      className="text-stone-700 focus:bg-[#EFB342]/20 focus:text-stone-900"
                    >
                      <div className="flex items-center gap-2">
                        <History size={14} />
                        {month.display_name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Leaderboard Table */}
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-stone-200">
                    <th className="text-left p-3 text-xs text-stone-500 uppercase font-semibold">Rank</th>
                    <th className="text-left p-3 text-xs text-stone-500 uppercase font-semibold">User</th>
                    <th className="text-center p-3 text-xs text-stone-500 uppercase font-semibold">Points</th>
                    <th className="text-center p-3 text-xs text-stone-500 uppercase font-semibold">Days Active</th>
                    {selectedMonth === 'current' && (
                      <th className="text-center p-3 text-xs text-stone-500 uppercase font-semibold">Streak</th>
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-800">
                  {displayedLeaderboard && displayedLeaderboard.length > 0 ? (
                    displayedLeaderboard.slice(0, 10).map((entry) => (
                      <tr 
                        key={entry.user_id}
                        className="hover:bg-[#F0EBE1] transition-colors cursor-pointer"
                        onClick={() => navigate(`/admin/users/${entry.user_id}`)}
                      >
                        <td className="p-3">
                          <div className="flex items-center gap-2">
                            {entry.rank === 1 && <Crown className="text-yellow-500" size={18} />}
                            {entry.rank === 2 && <Crown className="text-stone-600" size={18} />}
                            {entry.rank === 3 && <Crown className="text-amber-700" size={18} />}
                            <span className={`font-bold ${entry.rank <= 3 ? 'text-[#EFB342]' : 'text-stone-600'}`}>
                              #{entry.rank}
                            </span>
                          </div>
                        </td>
                        <td className="p-3">
                          <div>
                            <p className="font-medium text-stone-900">{entry.user_name}</p>
                            <p className="text-xs text-stone-500">{entry.email}</p>
                          </div>
                        </td>
                        <td className="p-3 text-center">
                          <span className="font-bold text-[#EFB342]">{entry.monthly_points}</span>
                        </td>
                        <td className="p-3 text-center">
                          <span className="text-stone-600">{entry.check_in_days}</span>
                        </td>
                        {selectedMonth === 'current' && (
                          <td className="p-3 text-center">
                            <span className="text-orange-500">{entry.current_streak || 0} 🔥</span>
                          </td>
                        )}
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={selectedMonth === 'current' ? 5 : 4} className="p-8 text-center text-stone-500">
                        No leaderboard data for this period
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* This Week's Intentions */}
        <AdminWeeklyIntentions />

        {/* Quick Links */}
        <div className="mb-8">
          <h2 className="font-heading text-xl font-bold text-stone-900 mb-4">QUICK ACTIONS</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {quickLinks.map((link, i) => (
              <Link
                key={link.to}
                to={link.to}
                className="bg-white border border-stone-200 rounded-2xl p-6 hover:border-[#EFB342]/50 transition-all duration-300 card-hover animate-fade-in"
                style={{ animationDelay: `${i * 0.1}s` }}
                data-testid={`quick-link-${link.label.toLowerCase().replace(/\s/g, '-')}`}
              >
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${link.color}`}>
                  <link.icon size={24} />
                </div>
                <p className="font-semibold text-stone-900">{link.label}</p>
              </Link>
            ))}
          </div>
        </div>

        {/* Tips Section */}
        <div className="bg-white border border-stone-200 rounded-2xl p-6">
          <h2 className="font-heading text-xl font-bold text-stone-900 mb-4">GETTING STARTED</h2>
          <ul className="space-y-3 text-stone-600">
            <li className="flex items-start gap-3">
              <span className="text-[#EFB342] font-bold">1.</span>
              <span>Create a <strong className="text-stone-900">Challenge</strong> to set the 28-day timeframe</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-[#EFB342] font-bold">2.</span>
              <span>Define <strong className="text-stone-900">Actions</strong> that users can check off daily (water, protein, etc.)</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-[#EFB342] font-bold">3.</span>
              <span>Set up <strong className="text-stone-900">Milestones</strong> to reward users for streaks</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-[#EFB342] font-bold">4.</span>
              <span>Add <strong className="text-stone-900">Daily Tips</strong> with GIFs and external links</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-[#EFB342] font-bold">5.</span>
              <span>Configure <strong className="text-stone-900">Automations</strong> to re-engage inactive users</span>
            </li>
          </ul>
        </div>
      </div>

      {/* GIF Celebration Modal */}
      <GifCelebrationModal
        isOpen={showGifModal}
        onClose={() => setShowGifModal(false)}
        gifUrl={testGifUrl}
      />
    </Layout>
  );
};

export default AdminDashboard;
